import { type RouteRecordRaw } from 'vue-router';
import { isProduction } from '@/utils/environment';

const AdministrationContainer = () =>
  import('@/contexts/administration/AdministrationContainer.vue');
const BackofficeActions = () =>
  import('@/contexts/administration/backofficeActions/BackofficeActions.vue');
const AdminUsersContainer = () =>
  import('@/contexts/administration/users/AdminUsersContainer.vue');
const MainErrorComponent = () =>
  import('@/components/errorHandling/MainErrorComponent.vue');
const AppAccountsContainer = () =>
  import('@/contexts/administration/appAccounts/AppAccountsContainer.vue');
const TourCommanderToursList = () =>
  import('@/modules/tourCommander/components/toursList/ToursList.vue');
const TourCommanderTourViewContainer = () =>
  import('@/modules/tourCommander/components/tourView/TourViewContainer.vue');
const TourCommanderTenantConfigContainer = () =>
  import(
    '@/modules/tourCommander/components/tenantConfig/TenantConfigContainer.vue'
  );
const QuickSightDashboards = () =>
  import('@/components/dashboards/QuickSightDashboards.vue');
const UsersList = () => import('@/components/users/UsersList.vue');
const TenantsContainer = () =>
  import('@/components/configurations/TenantContainer.vue');
const DepotsContainer = () =>
  import('@/components/configurations/DepotContainer.vue');
const TenantsList = () =>
  import('@/components/configurations/tenants/TenantsList.vue');
const TenantDetails = () =>
  import('@/components/configurations/tenants/TenantDetails.vue');
const TenantCoreData = () =>
  import('@/components/configurations/tenants/TenantCoreData.vue');
const TenantConfiguration = () =>
  import('@/components/configurations/tenants/TenantConfiguration.vue');
const TenantConfigurations = () =>
  import('@/components/configurations/tenants/TenantConfigurations.vue');
const DepotsList = () =>
  import('@/components/configurations/depots/DepotList.vue');
const DepotDetails = () =>
  import('@/components/configurations/depots/DepotDetails.vue');
const DepotCoreData = () =>
  import('@/components/configurations/depots/DepotCoreData.vue');
const DepotConfiguration = () =>
  import('@/components/configurations/depots/DepotConfiguration.vue');
const DepotConfigurations = () =>
  import('@/components/configurations/depots/DepotConfigurations.vue');
const DepotConfigurationsHistory = () =>
  import('@/components/configurations/depots/DepotConfigurationsHistory.vue');
const AdministrationContextNavigation = () =>
  import('@/contexts/administration/AdministrationContextNavigation.vue');

const adminChildRoutes: RouteRecordRaw[] = [
  {
    component: BackofficeActions,
    name: 'backofficeActions',
    path: 'backoffice-actions',
    meta: {
      titleKey: 'mainNavigation.backofficeActions'
    }
  },
  {
    component: TourCommanderToursList,
    name: 'tourCommanderToursList',
    path: 'tour-commander/tour/list',
    meta: {
      titleKey: 'mainNavigation.tourView'
    }
  },
  {
    component: TourCommanderTourViewContainer,
    name: 'tourCommanderTourViewContainer',
    path: 'tour-commander/tour/view/:tourId',
    props: true,
    meta: {
      titleKey: 'mainNavigation.tourView'
    }
  },
  {
    component: TourCommanderTenantConfigContainer,
    name: 'TourCommanderTenantConfig',
    path: 'tour-commander/tenant-config',
    meta: {
      titleKey: 'mainNavigation.tenantConfiguration'
    }
  },
  {
    component: QuickSightDashboards,
    name: 'dashboards',
    path: 'reports'
  },
  {
    component: AppAccountsContainer,
    name: 'appAccountsAdmin',
    path: 'app-accounts-admin',
    meta: {
      titleKey: 'mainNavigation.secondScreenAccounts'
    }
  },
  {
    children: [
      {
        components: {
          configDepots: DepotsList
        },
        name: 'configDepotsList',
        path: '',
        meta: {
          titleKey: 'mainNavigation.contexts.depots'
        }
      },
      {
        children: [
          {
            components: {
              configDepotCoreData: DepotCoreData
            },
            name: 'configDepotCoreData',
            path: 'details'
          },
          {
            components: {
              configDepotConfiguration: DepotConfiguration
            },
            name: 'configDepotConfiguration',
            path: 'configuration'
          },
          {
            components: {
              configDepotConfigurations: DepotConfigurations
            },
            name: 'configDepotConfigurations',
            path: 'configurations'
          },
          {
            components: {
              configDepotConfigurationHistory: DepotConfigurationsHistory
            },
            name: 'configDepotConfigurationHistory',
            path: 'configuration-history'
          }
        ],
        components: {
          configDepotDetails: DepotDetails
        },
        path: ':internalDepotId',
        props: true,
        meta: {
          titleKey: 'mainNavigation.contexts.depots'
        }
      }
    ],
    component: DepotsContainer,
    path: 'configurations/depots'
  },
  {
    children: [
      {
        components: {
          configTenants: TenantsList
        },
        name: 'configTenantsList',
        path: '',
        meta: {
          titleKey: 'mainNavigation.contexts.tenants'
        }
      },
      {
        children: [
          {
            components: {
              configTenantCoreData: TenantCoreData
            },
            name: 'configTenantCoreData',
            path: 'details',
            props: true
          },
          {
            components: {
              configTenantConfiguration: TenantConfiguration
            },
            name: 'configTenantConfiguration',
            path: 'configuration',
            props: true
          },
          {
            components: {
              configTenantConfigurations: TenantConfigurations
            },
            name: 'configTenantConfigurations',
            path: 'configurations',
            props: true
          }
        ],
        components: {
          configTenantDetails: TenantDetails
        },
        path: ':tenantId',
        props: true,
        meta: {
          titleKey: 'mainNavigation.contexts.tenants'
        }
      }
    ],
    component: TenantsContainer,
    path: 'configurations/organizations'
  },
  {
    children: [
      {
        components: {
          backofficeUsersAdmin: UsersList
        },
        name: 'backofficeUsersAdmin',
        path: 'backoffice'
      },
      {
        components: {
          userErrorAdmin: MainErrorComponent
        },
        name: 'userErrorAdmin',
        path: 'error/:errorCode',
        props: {
          userError: true
        }
      }
    ],
    component: AdminUsersContainer,
    name: 'globalUsers',
    path: 'users',
    meta: {
      titleKey: 'userManagement.names.backofficeUser'
    }
  },
  ...(!isProduction
    ? [
        {
          component: () =>
            import('@/contexts/administration/apiDocs/ApiDocsContainer.vue'),
          name: 'apiDocs',
          path: 'api-docs',
          meta: {
            titleKey: 'mainNavigation.apiDocumentation'
          }
        } as RouteRecordRaw
      ]
    : [])
];

export const adminRoute: RouteRecordRaw = {
  components: {
    default: AdministrationContainer,
    LeftSidebar: AdministrationContextNavigation
  },
  name: 'administration',
  path: '/administration',
  redirect: '/administration/users/backoffice',
  children: adminChildRoutes
};
